import React from "react";
import { Phone, ChevronUp } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { splitLangPath, withLang } from "../i18n/urls";
import { COMPANY, FOOTER_LINKS } from "../data/mock";

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

const Footer = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const lang = splitLangPath(pathname).lang;
  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <footer id="contact" className="relative bg-black border-t border-white/10 pt-16 md:pt-24 pb-8">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        {/* Top: contact */}
        <div className="text-center mb-12">
          <p className="text-[10px] tracking-[0.5em] uppercase text-arabell-gold mb-3">
            {t("footer.get_in_touch")}
          </p>
          <h3 className="font-display text-3xl md:text-5xl text-white tracking-[0.15em] uppercase mb-8">
            {t("footer.contact")}
          </h3>
          <a
            href={`https://wa.me/${COMPANY.whatsappRaw}?text=Salve%2C+vorrei+avere+maggiori+informazioni+sui+servizi+Arabell+Group.`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-3 text-white/90 hover:text-arabell-gold transition-colors duration-300"
          >
            <WhatsAppIcon className="w-6 h-6 text-[#25D366]" />
            <span className="font-display text-xl md:text-2xl tracking-[0.2em]">
              {COMPANY.whatsapp}
            </span>
          </a>
          <div className="mt-3">
            <a
              href={`mailto:${COMPANY.email}`}
              className="text-xs tracking-[0.3em] uppercase text-white/50 hover:text-white"
            >
              {COMPANY.email}
            </a>
          </div>
        </div>

        {/* Legal data */}
        <div className="text-center max-w-2xl mx-auto pb-10 border-b border-white/10">
          <p className="text-sm md:text-base text-white tracking-[0.18em] uppercase mb-3">
            {COMPANY.shortName}
          </p>
          <p className="text-xs md:text-sm text-white/60 leading-relaxed mb-1">
            {t("footer.legal_office")}: {COMPANY.legalAddress}
          </p>
          <p className="text-xs md:text-sm text-white/60 leading-relaxed mb-1">
            {t("footer.share_capital")}: {COMPANY.capitaleSociale}
          </p>
          <p className="text-xs md:text-sm text-white/60 leading-relaxed mb-1">
            P.IVA / C.F.: {COMPANY.pIva}
          </p>
          <p className="text-xs md:text-sm text-white/60 leading-relaxed mb-1">
            REA: {COMPANY.rea}
          </p>
          <p className="text-xs md:text-sm text-white/60 leading-relaxed mb-1">
            {t("footer.pec")}:{" "}
            <a href={`mailto:${COMPANY.pec}`} className="hover:text-arabell-gold">
              {COMPANY.pec}
            </a>
          </p>
        </div>

        {/* Links */}
        <div className="py-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 border-b border-white/10">
          {FOOTER_LINKS.primary.map((l) => (
            <Link
              key={l.label}
              to={withLang(l.href, lang)}
              className="text-xs tracking-[0.3em] uppercase text-white/70 hover:text-arabell-gold transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="py-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 border-b border-white/10">
          {FOOTER_LINKS.secondary.map((l) => (
            <Link
              key={l.label}
              to={withLang(l.href, lang)}
              className="text-[11px] tracking-[0.3em] uppercase text-white/50 hover:text-white transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="py-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
          {FOOTER_LINKS.legal.map((l) => (
            <Link
              key={l.label}
              to={withLang(l.href, lang)}
              className="text-[10px] tracking-[0.3em] uppercase text-white/40 hover:text-white transition-colors"
            >
              {l.label}
            </Link>
          ))}
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent("arabell-open-cookie-prefs"))}
            data-testid="footer-manage-cookies"
            className="text-[10px] tracking-[0.3em] uppercase text-white/40 hover:text-arabell-gold transition-colors"
          >
            {t("footer.manage_cookies")}
          </button>
        </div>

        {/* Bottom */}
        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <img
            src={COMPANY.logoUrl}
            alt="Arabell"
            className="h-10 w-auto"
            style={{ filter: "invert(1) brightness(1.1)" }}
            loading="lazy"
            decoding="async"
          />
          <p className="text-[11px] text-white/50 tracking-[0.2em] text-center">
            {COMPANY.name} — {COMPANY.tagline}
            <br className="md:hidden" />
            <span className="hidden md:inline"> · </span>
            © 2026 {COMPANY.name}. {t("footer.rights")}.
          </p>
          <button
            onClick={scrollTop}
            className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-white/60 hover:text-arabell-gold transition-colors"
          >
            {t("footer.back_to_top")} <ChevronUp className="w-4 h-4" strokeWidth={1.4} />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
